import Utils from './utils';
import Constants from './constants';
import * as Formulas from './formulas';

/**
 * Input field validation
 */
export const OPERATING_TEMPERATURE = (validatedValue, metadata, model) => {
  const minValidationValue = model.signalChainHandler.dac.selectedTemperatureRange.min,
    maxValidationValue = model.signalChainHandler.dac.selectedTemperatureRange.max;

  return PROCESS_VALIDATIONS(metadata, [
    MIN(validatedValue, minValidationValue, metadata),
    MAX(validatedValue, maxValidationValue, metadata),
  ]);
};

export const POWER_SUPPLY_RIPPLE = (validatedValue, metadata) => {
  const MIN_VALIDATION_VALUE = 0;
  const MAX_VALIDATION_VALUE = 1;

  return PROCESS_VALIDATIONS(metadata, [
    MIN(validatedValue, MIN_VALIDATION_VALUE, metadata),
    MAX(validatedValue, MAX_VALIDATION_VALUE, metadata),
  ]);
};

/**
 * Resistor Validations: GAIN, RESISTOR, TOLERANCE, TEMPCO
 */
export const GAIN = (resistor, metadata, model) => {
  const vout = model.signalChainHandler.inputVoltage,
    dac = model.signalChainHandler.dac,
    resistorValidation = resistor
      ? resistor.isPositive
        ? dac.selectedVrefPosExternal
        : dac.selectedVrefNegExternalOrVrefPosExternal
      : null,
    amplifierGain = resistor.amplifierGain,
    calculatedValidatedValue = Utils.isNumeric(amplifierGain) && Utils.isNumeric(vout) ? amplifierGain * vout : null;

  if (
    !(
      (resistorValidation.min.name || Utils.isNumeric(resistorValidation.min.displayValue)) &&
      (resistorValidation.max.name || Utils.isNumeric(resistorValidation.max.displayValue))
    )
  ) {
    console.warn(`${model.signalChainHandler.dac.selected.name}: the Gain validation parameters could not be found`);
    return PROCESS_VALIDATIONS(metadata, []);
  }

  const args = {
      suffix: metadata.suffix,
      inputVoltage: DISPLAY_NUMBER(vout, metadata.displayFn),
      ampGain: DISPLAY_NUMBER(amplifierGain, metadata.displayFn),
      calculated: DISPLAY_NUMBER(calculatedValidatedValue, metadata.displayFn),
    },
    minArgs = Object.assign({}, args, {
      expectedField: resistorValidation.min.name,
      expected: resistorValidation.min.displayValue,
    }),
    maxArgs = Object.assign({}, args, {
      expectedField: resistorValidation.max.name,
      expected: resistorValidation.max.displayValue,
    });

  return PROCESS_VALIDATIONS(metadata, [
    MIN(calculatedValidatedValue, resistorValidation.min.value, metadata, 'messages.resistor_min', minArgs),
    MAX(calculatedValidatedValue, resistorValidation.max.value, metadata, 'messages.resistor_max', maxArgs),
  ]);
};

export const RESISTOR = (validatedValue, metadata) => {
  const MIN_VALIDATION_VALUE = 1;
  const MAX_VALIDATION_VALUE = 1000000000000; // '1T'

  return PROCESS_VALIDATIONS(metadata, [
    MIN(validatedValue, MIN_VALIDATION_VALUE, metadata),
    MAX(validatedValue, MAX_VALIDATION_VALUE, metadata),
  ]);
};

export const TOLERANCE = (validatedValue, metadata) => {
  const MIN_VALIDATION_VALUE = 0;
  const MAX_VALIDATION_VALUE = 20;

  return PROCESS_VALIDATIONS(metadata, [
    MIN(validatedValue, MIN_VALIDATION_VALUE, metadata),
    MAX(validatedValue, MAX_VALIDATION_VALUE, metadata),
  ]);
};

export const TEMPCO = (validatedValue, metadata) => {
  const MIN_VALIDATION_VALUE = 0;
  const MAX_VALIDATION_VALUE = 5000;

  return PROCESS_VALIDATIONS(metadata, [
    MIN(validatedValue, MIN_VALIDATION_VALUE, metadata),
    MAX(validatedValue, MAX_VALIDATION_VALUE, metadata),
  ]);
};

export const FIND_RESISTOR_NETWORK_PART = (validatedResistor, metadata, model) => {
  const resistorValue1 = validatedResistor.values.res1,
    resistorValue2 = validatedResistor.values.res2,
    resistorTolerance = validatedResistor.values.tolerance;

  let partNumber = undefined,
    productPage = undefined;

  const resistorNetworkParts = [
    {
      partNumber: 'LT5400A-1',
      minValue: 4.9e3,
      maxValue: 20.1e3,
      minTolerance: 0.025,
      maxTolerance: 0.05,
      generic: 'LT5400',
    },
    {
      partNumber: 'LT5400A-2',
      minValue: 49e3,
      maxValue: 201e3,
      minTolerance: 0.025,
      maxTolerance: 0.05,
      generic: 'LT5400',
    },
    {
      partNumber: 'LT5400A-4',
      minValue: 490,
      maxValue: 2010,
      minTolerance: 0.025,
      maxTolerance: 0.05,
      generic: 'LT5400',
    },
    {
      partNumber: 'LT5400A-5',
      minValue: 490e3,
      maxValue: 2.01e6,
      minTolerance: 0.025,
      maxTolerance: 0.05,
      generic: 'LT5400',
    },
    {
      partNumber: 'LT5400B-1',
      minValue: 4.9e3,
      maxValue: 20.1e3,
      minTolerance: 0.01,
      maxTolerance: 0.025,
      generic: 'LT5400',
    },
    {
      partNumber: 'LT5400B-2',
      minValue: 49e3,
      maxValue: 201e3,
      minTolerance: 0.01,
      maxTolerance: 0.025,
      generic: 'LT5400',
    },
    {
      partNumber: 'LT5400B-4',
      minValue: 490,
      maxValue: 2010,
      minTolerance: 0.01,
      maxTolerance: 0.025,
      generic: 'LT5400',
    },
    {
      partNumber: 'LT5400B-5',
      minValue: 490e3,
      maxValue: 2.01e6,
      minTolerance: 0.01,
      maxTolerance: 0.025,
      generic: 'LT5400',
    },
  ];

  if (resistorValue1 === resistorValue2) {
    for (let i = 0; i < resistorNetworkParts.length; i++) {
      //Note that at on the low end we are allowed to equal the tolerance value, but on the high end we are not.
      if (
        resistorTolerance >= resistorNetworkParts[i].minTolerance &&
        resistorTolerance < resistorNetworkParts[i].maxTolerance
      ) {
        if (resistorValue1 > resistorNetworkParts[i].minValue && resistorValue1 < resistorNetworkParts[i].maxValue) {
          partNumber = resistorNetworkParts[i].partNumber;
          productPage = `${model.urlHandler.homeUrl}/${resistorNetworkParts[i].generic}`;
        }
      }
    }
  }

  return PROCESS_VALIDATIONS(metadata, [
    {
      isValid: !!partNumber,
      translation: 'messages.resistor_network_part',
      translationArgs: {
        part: partNumber,
        productPage: productPage,
      },
    },
  ]);
};

export const TEMPERATURE_RANGE = (temperatureRange, metadata, model) => {
  const minOperatingTemperature = model.signalChainHandler.dac.data.operatingTemperature.min,
    maxOperatingTemperature = model.signalChainHandler.dac.data.operatingTemperature.max;

  const args = {
    suffix: metadata.suffix,
    expectedField: metadata.fieldName,
    itemName: metadata.itemName,
  };
  let minArgs = {
      value: temperatureRange.min,
      expected: minOperatingTemperature,
    },
    maxArgs = {
      value: temperatureRange.max,
      expected: maxOperatingTemperature,
    };

  Object.assign(minArgs, args);
  Object.assign(maxArgs, args);

  return PROCESS_VALIDATIONS(metadata, [
    // The min operating temperature should not be below the signal chain item's min temp
    MIN(minOperatingTemperature, temperatureRange.min, metadata, 'messages.temp_range_min', minArgs),
    // The max operating temperature should not exceed the signal chain item's max temp
    MAX(maxOperatingTemperature, temperatureRange.max, metadata, 'messages.temp_range_max', maxArgs),
  ]);
};

export const VOLTAGE_REFERENCE = (metadata, model) => {
  if (model && model.signalChainHandler && model.voltageMap) {
    let validationItems = {},
      validations = [];
    const voltageMap = model.voltageMap;

    validationItems[Constants.OUTPUT_VOLTAGE.POSITIVE] = model.signalChainHandler.dac.selectedVrefPosExternal;
    validationItems[Constants.OUTPUT_VOLTAGE.NEGATIVE] =
      model.signalChainHandler.dac.selectedVrefNegExternalOrVrefPosExternal;

    Object.keys(voltageMap).forEach((key) => {
      if (!(key in validationItems)) return;

      const validationItem = validationItems[key];
      if (
        !(
          (validationItem.min.name || Utils.isNumeric(validationItem.min.value)) &&
          (validationItem.max.name || Utils.isNumeric(validationItem.max.value))
        )
      ) {
        console.warn(
          `${model.signalChainHandler.dac.selected.name}: the Voltage Reference validation parameters could not be found`,
        );
        return;
      }

      const outputVoltage = voltageMap[key].voltage,
        minValidationItem = validationItem.min,
        maxValidationItem = validationItem.max,
        args = {
          value: DISPLAY_NUMBER(outputVoltage, metadata.displayFn),
          type: key in metadata.typeMap ? metadata.typeMap[key] : '',
          suffix: metadata.suffix,
        },
        minArgs = Object.assign({}, args, {
          expectedField: minValidationItem.name,
          expected: minValidationItem.displayValue,
        }),
        maxArgs = Object.assign({}, args, {
          expectedField: maxValidationItem.name,
          expected: maxValidationItem.displayValue,
        });

      validations.push(MIN(outputVoltage, minValidationItem.value, metadata, 'messages.min_vout', minArgs));
      validations.push(MAX(outputVoltage, maxValidationItem.value, metadata, 'messages.max_vout', maxArgs));
    });

    return PROCESS_VALIDATIONS(metadata, validations);
  } else return [];
};

/**
 * DAC / Topology Input validations
 */

export const DAC_TYPE = (metadata, model) => {
  const warnings = model.signalChainHandler.dac.type.warnings;
  return PROCESS_VALIDATIONS(
    metadata,
    (warnings || []).map((warning) => warning(model)),
  );
};

export const DAC_HAS_INTERNAL_INPUT_AND_TOPOLOGY_INPUT_IS_BUFFERED = (key, item, input, translation) => {
  let result = false;

  const hasInternalInput = Formulas.GET_CDP_BOOLEAN_VALUE(key, item).value,
    bufferIsSelected = input && input.isSelectedOptionBuffered ? true : false;

  if (hasInternalInput && bufferIsSelected) result = true;

  return {
    isValid: !result,
    translation: translation,
    translationArgs: { dac: item ? item.name : '' },
  };
};

/**
 * Internal functions
 */

const MIN = (validatedValue, validationValue, metadata, translation, translationArgs) => {
  const args = translationArgs || {
    field: metadata.fieldName,
    value: DISPLAY_NUMBER(validationValue, metadata.displayFn),
    suffix: metadata.suffix,
  };

  return BASE_VALIDATION(
    validatedValue,
    validationValue,
    translation || 'messages.min',
    args,
    (validatedValue, validationValue) => validatedValue >= validationValue,
  );
};

const MAX = (validatedValue, validationValue, metadata, translation, translationArgs) => {
  const args = translationArgs || {
    field: metadata.fieldName,
    value: DISPLAY_NUMBER(validationValue, metadata.displayFn),
    suffix: metadata.suffix,
  };

  return BASE_VALIDATION(
    validatedValue,
    validationValue,
    translation || 'messages.max',
    args,
    (validatedValue, validationValue) => validatedValue <= validationValue,
  );
};

const BASE_VALIDATION = (validatedValue, validationValue, translation, translationArgs, validation) => {
  return {
    isValid:
      !Utils.isNullOrWhiteSpace(validationValue) && !Utils.isNullOrWhiteSpace(validatedValue)
        ? validation(validatedValue, validationValue)
        : false,
    translation: translation,
    translationArgs: translationArgs,
  };
};

const DISPLAY_NUMBER = (value, displayFn) => {
  return Utils.isNumeric(value) && displayFn ? displayFn(value) : value;
};

const PROCESS_VALIDATIONS = (metadata, validations) => {
  /**
   * This doesn't do much now, but it is a way to synchronize
   * what we are returning from each validation and possibly
   * make changes later.
   */
  return {
    key: metadata.key,
    tab: metadata.tab,
    showAllInvalid: metadata.showAllInvalid || false,
    isWarning: metadata.isWarning,
    isInfo: metadata.isInfo,
    results: validations,
  };
};
